import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  documentFontFamilies,
  injectIntoHead,
  reportFontOverrideCss,
} from '@realadvisor/common-types';
import { buildFinalContent } from '@realadvisor/template-renderer/content-builder';

import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { type ReportEditorPerformanceTracker } from '@modules/_shared/components/report-editor/useReportEditorPerformance';
import { createTemplateRenderer } from '@modules/_shared/utils/createTemplateRenderer';
import { createDataUrl } from '@modules/_shared/utils/document-url';
import {
  loadPdfPreviewSanitizer,
  sanitizeLikePdf,
} from '@modules/_shared/utils/pdfPreviewSanitizer';
import { GOOGLE_MAPS_TOKEN, docs_direct_origin } from '@shared/config';
import { useLocale } from '@shared/hooks/locale';

type UseTemplateRendererProps = {
  key: string | number;
  performanceTracker?: ReportEditorPerformanceTracker;
};

const FETCH_TIMEOUT_MS = 30_000;
const FONT_TIMEOUT_MS = 10_000;

const themeFontCss = new Map<string, Promise<string>>();

const loadThemeFontCss = (family: string): Promise<string> => {
  const cached = themeFontCss.get(family);
  if (cached != null) {
    return cached;
  }
  const url = new URL('/fonts/css', docs_direct_origin);
  url.searchParams.set('family', family);
  const css = fetch(url, { signal: AbortSignal.timeout(FONT_TIMEOUT_MS) })
    .then(res => (res.ok ? res.text() : ''))
    .catch(() => '')
    .then(text => {
      if (text === '' || text.includes('<')) {
        themeFontCss.delete(family);
        return '';
      }
      return text;
    });
  themeFontCss.set(family, css);
  return css;
};

const describeError = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

export const useTemplateRenderer = ({
  key,
  performanceTracker,
}: UseTemplateRendererProps) => {
  const { countryCode, t } = useLocale();
  const hasFetchedRef = useRef(false);
  const lastMarkupRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [markup, setMarkup] = useState<null | string>(null);
  const [data, setData] = useState<{
    docTemplateThemeContent?: string | null;
    cmaReport?: { fontFamily?: string | null } | null;
    lot?: { brochureFontFamily?: string | null } | null;
  } | null>(null);

  const retry = useCallback(() => {
    setHasError(false);
    setErrorDetail(null);
    setAttempt(current => current + 1);
  }, []);

  const {
    language,
    descriptionLanguage,
    data: reportData,
    documentId,
    documentType,
    previewOnly,
  } = useReportContext();

  const translations = useMemo(
    () => reportData?.doc_template?.translations?.[language],
    [reportData, language],
  );

  const renderers = useMemo(
    () => ({
      document: createTemplateRenderer({
        countryCode,
        language,
        translations,
        sanitize: sanitizeLikePdf,
      }),
      text: createTemplateRenderer({ countryCode, language, translations }),
    }),
    [language, countryCode, translations],
  );

  useEffect(() => {
    if (documentId == null || documentType == null) {
      return;
    }

    if (hasFetchedRef.current) {
      performanceTracker?.startEditTracking();
    }
    hasFetchedRef.current = true;

    let cancelled = false;
    // Abort hung requests (e.g. render service down) so the preview can show
    // an error state instead of spinning forever.
    const abortController = new AbortController();
    const timeoutId = setTimeout(
      () => abortController.abort(),
      FETCH_TIMEOUT_MS,
    );

    async function fetchData() {
      try {
        setIsLoading(true);
        setHasError(false);
        const dataUrl = createDataUrl(
          documentId,
          documentType,
          language,
          descriptionLanguage,
        );
        // A read-only preview does not fetch every editable relation. Its
        // fingerprint cannot prove freshness; let docs check the saved data.
        if (!previewOnly) {
          dataUrl.searchParams.set('v', String(key));
        }
        const res = await fetch(dataUrl, {
          method: 'GET',
          credentials: 'include',
          signal: abortController.signal,
        });
        const result = await res.json();

        if (cancelled) {
          return;
        }

        if (result.error) {
          console.error(result.error);
          setErrorDetail(describeError(result.error));
          setHasError(true);
          performanceTracker?.cancelEditTracking();
        } else {
          setData(result.data);
          performanceTracker?.onDataFetchComplete();
        }
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          setErrorDetail(describeError(error));
          setHasError(true);
          performanceTracker?.cancelEditTracking();
        }
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [
    key,
    attempt,
    documentId,
    documentType,
    language,
    descriptionLanguage,
    previewOnly,
    performanceTracker,
  ]);

  const renderTemplate = useCallback(
    async ({
      content,
      meta,
      kind = 'document',
    }: {
      content: string;
      meta?: Record<string, unknown>;
      kind?: 'document' | 'text';
    }) => {
      if (!data) {
        return undefined;
      }

      try {
        if (kind === 'document') {
          await loadPdfPreviewSanitizer();
        }
        const result = await renderers[kind].render({
          content,
          data,
          meta: {
            language,
            ...meta,
          },
        });
        return result;
      } catch (error) {
        console.error('Template rendering failed:', error);
        setErrorDetail(describeError(error));
        return undefined;
      }
    },
    [data, language, renderers],
  );

  const themeTemplateContent =
    reportData.doc_template_theme?.content ??
    (reportData.doc_template_theme_id != null
      ? data?.docTemplateThemeContent
      : null);

  const reportFont =
    data?.cmaReport?.fontFamily ?? data?.lot?.brochureFontFamily;

  useEffect(() => {
    let cancelled = false;
    const renderDocument = async () => {
      if (!data) {
        return;
      }
      if (!reportData.doc_template?.content) {
        setMarkup(null);
        setErrorDetail(t('This document template is empty.'));
        setHasError(true);
        return;
      }
      try {
        const finalContent = buildFinalContent({
          templateContent: reportData.doc_template?.content,
          themeTemplateContent,
        });

        const [rendered, fontCss] = await Promise.all([
          renderTemplate({
            content: finalContent,
            meta: {
              googleMapsChannel: 'crm',
              googleMapsKey: GOOGLE_MAPS_TOKEN,
            },
          }),
          Promise.all(
            documentFontFamilies(themeTemplateContent, reportFont).map(
              loadThemeFontCss,
            ),
          ),
        ]);
        if (cancelled) {
          return;
        }
        const fontOverrideCss = reportFontOverrideCss(reportFont);
        const fontStyle = [...fontCss, fontOverrideCss]
          .filter(Boolean)
          .join('\n');
        const markup =
          rendered != null && fontStyle !== ''
            ? injectIntoHead(rendered, `<style>${fontStyle}</style>`)
            : rendered;

        if (markup != null) {
          if (markup === lastMarkupRef.current) {
            performanceTracker?.cancelEditTracking();
          } else {
            performanceTracker?.onTemplateRenderComplete();
          }
          lastMarkupRef.current = markup;
          setMarkup(markup);
          setErrorDetail(null);
          setHasError(false);
        } else {
          setMarkup(null);
          setHasError(true);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('Template rendering failed:', error);
        setMarkup(null);
        setErrorDetail(describeError(error));
        setHasError(true);
      }
    };

    renderDocument();
    return () => {
      cancelled = true;
    };
  }, [
    data,
    reportData.doc_template?.content,
    renderTemplate,
    themeTemplateContent,
    reportFont,
    t,
    performanceTracker,
  ]);

  const renderFilename = useCallback(async () => {
    if (!reportData?.doc_template?.filename_template || !data) {
      return '';
    }

    try {
      const renderedFilename = await renderTemplate({
        content: reportData.doc_template.filename_template,
        kind: 'text',
      });
      return renderedFilename?.trim() || '';
    } catch (error) {
      console.error('Failed to render filename template:', error);
      return '';
    }
  }, [data, reportData?.doc_template?.filename_template, renderTemplate]);

  return { markup, isLoading, hasError, errorDetail, retry, renderFilename };
};
